import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IoArrowBack,
  IoTrashOutline,
  IoDocumentTextOutline,
  IoAddCircleOutline,
  IoCloudUploadOutline,
  IoToggle,
  IoEllipsisVertical,
  IoCheckmarkCircle,
  IoCloseCircle,
  IoClose,
} from 'react-icons/io5';
import { HiOutlineTemplate } from 'react-icons/hi';
import { Loader2, Plus } from 'lucide-react';
import toast from 'react-hot-toast';

import { DataState } from '../../context/DataProvider';
import {
  listReportTemplates,
  deleteReportTemplate,
  toggleReportTemplateStatus,
  getReportTemplate,
  autoParseTemplate,
} from '../../services/reportTemplateApi';
import {
  initializeStreamChat,
  streamChat,
  createTask,
} from '../../services/api';

// =========================================================================
// SectionEditForm — multi-field edit form for sections
// =========================================================================
const SectionEditForm = ({
  initialTitle = '',
  initialInstruction = '',
  initialStyling = '',
  onSubmit,
  onCancel,
  isLoading,
  isEdit = false,
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [instruction, setInstruction] = useState(initialInstruction);
  const [styling, setStyling] = useState(initialStyling);
  const titleRef = useRef(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  const handleSubmit = () => {
    if (!title.trim() || isLoading) return;
    onSubmit({
      title: title.trim(),
      instruction: instruction.trim(),
      styling: styling.trim(),
    });
  };

  return (
    // eslint-disable-next-line
    <div
      className="absolute left-1/2 z-30 w-[90%] max-w-[500px] -translate-x-1/2"
      style={{ top: '100%' }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="mt-2 flex flex-col gap-3 rounded-xl border border-indigo-300 bg-white p-4 shadow-xl shadow-indigo-100/40">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-wider text-gray-700">
            {isEdit ? 'Edit Section' : 'New Section'}
          </span>
          <button
            onClick={onCancel}
            className="text-gray-400 transition-colors hover:text-gray-600"
          >
            <IoClose className="text-sm" />
          </button>
        </div>

        <div>
          <label
            htmlFor="session-title"
            className="mb-1 block text-[10px] font-semibold uppercase text-gray-500"
          >
            Section Title
          </label>
          <input
            ref={titleRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
              if (e.key === 'Escape') onCancel();
            }}
            placeholder="e.g. Executive Summary"
            className="w-full rounded border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-800 placeholder-gray-400 outline-none transition-colors focus:border-indigo-400"
            disabled={isLoading}
          />
        </div>

        <div>
          <label
            htmlFor="content"
            className="mb-1 block text-[10px] font-semibold uppercase text-gray-500"
          >
            Content / Sub-Points for AI
          </label>
          <textarea
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder="Add sub-points or data constraints for the AI..."
            className="min-h-[50px] w-full resize-y rounded border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-800 placeholder-gray-400 outline-none transition-colors focus:border-indigo-400"
            disabled={isLoading}
          />
        </div>

        <div>
          <label
            htmlFor="formatting-and-styling"
            className="mb-1 block text-[10px] font-semibold uppercase text-gray-500"
          >
            Formatting & Styling Requirements
          </label>
          <input
            value={styling}
            onChange={(e) => setStyling(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="e.g., Use bullet points, bold headers, table format..."
            className="w-full rounded border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-800 placeholder-gray-400 outline-none transition-colors focus:border-indigo-400"
            disabled={isLoading}
          />
        </div>

        <div className="mt-1 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!title.trim() || isLoading}
            className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-indigo-700 disabled:bg-indigo-400"
          >
            {isLoading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <IoCheckmarkCircle className="text-sm" />
            )}
            {isEdit ? 'Save Changes' : 'Create Section'}
          </button>
        </div>
      </div>
    </div>
  );
};

// =========================================================================
// Structural Template Preview Component
// =========================================================================
const TemplateStructuralPreview = ({
  templateName,
  header,
  footer,
  sections,
}) => {
  return (
    // eslint-disable-next-line
    <div
      className="flex justify-center px-4 py-8"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="flex w-full max-w-[816px] flex-col overflow-hidden rounded-sm bg-white text-black shadow-2xl shadow-black/40"
        style={{
          minHeight: '1056px',
          fontFamily: "'Inter', 'Roboto', sans-serif",
        }}
      >
        <div className="flex flex-1 flex-col gap-6 p-[72px]">
          {/* Header */}
          {header && (
            <div className="relative rounded border border-dashed border-gray-300 bg-gray-50/50 p-4">
              <span className="absolute -top-3 left-4 bg-white px-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                Document Header
              </span>
              {header.images && header.images.length > 0 && (
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex h-16 w-16 items-center justify-center rounded bg-gray-200 text-xs text-gray-500">
                    [Logo]
                  </div>
                </div>
              )}
              <div className="text-sm text-gray-600">
                {header.text ? (
                  header.text
                ) : (
                  <span className="italic text-gray-400">Header Content</span>
                )}
              </div>
            </div>
          )}

          {/* Title */}
          <div className="mb-8 mt-4 text-center">
            <h1 className="text-3xl font-bold text-gray-800">
              <span className="inline-block rounded-md border border-indigo-100 bg-indigo-50 px-3 py-1 font-mono text-xl text-indigo-700">
                {`{{ Template: ${templateName || 'Untitled'} }}`}
              </span>
            </h1>
          </div>

          {/* Sections */}
          <div className="flex flex-1 flex-col gap-8">
            {!sections || sections.length === 0 ? (
              <div className="flex flex-1 items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50">
                <p className="italic text-gray-400">
                  No sections defined yet. Add some structure on the right.
                </p>
              </div>
            ) : (
              sections.map((section, idx) => (
                <div
                  key={idx}
                  className="group relative rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-indigo-300 hover:shadow-md"
                >
                  <span className="absolute -top-3 left-4 bg-white px-2 text-[10px] font-bold uppercase tracking-wider text-indigo-400">
                    Section {idx + 1}
                  </span>

                  <h2 className="mb-3 flex items-center gap-2 text-xl font-bold text-gray-800">
                    <span className="inline-block whitespace-normal rounded border border-indigo-100/50 bg-indigo-50/80 px-2 py-1 font-mono text-sm leading-relaxed text-indigo-700">
                      {`{{ ${section.title || section.heading || 'Section Title'} }}`}
                    </span>
                  </h2>

                  <div className="flex min-h-[100px] flex-col items-start gap-3 rounded-md border border-gray-100 bg-gray-50 p-4">
                    <p className="font-mono text-sm leading-relaxed text-gray-500 opacity-60">
                      {`{{ AI Generated Text Context: ${section.description || section.content_summary || 'Detailed summary content for this section goes here.'} }}`}
                    </p>
                    {section.styling && (
                      <div className="mt-auto flex w-full items-center gap-2 border-t border-gray-100 pt-2">
                        <span className="text-[9px] font-bold uppercase tracking-tighter text-indigo-400">
                          Styling:
                        </span>
                        <span className="rounded-full border border-indigo-100/50 bg-indigo-50 px-2 py-0.5 text-[10px] font-medium italic text-indigo-600">
                          {section.styling}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {footer && (
            <div className="relative mt-8 rounded border border-dashed border-gray-300 bg-gray-50/50 p-4">
              <span className="absolute -top-3 left-4 bg-white px-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                Document Footer
              </span>
              <div className="flex items-center justify-between text-sm text-gray-600">
                <div>
                  {footer.text ? (
                    footer.text
                  ) : (
                    <span className="italic text-gray-400">Footer Text</span>
                  )}
                </div>
                <div className="rounded bg-gray-200 px-2 py-0.5 font-mono text-xs text-gray-500">
                  {`{{ Page Number }}`}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// =========================================================================
// Main Component
// =========================================================================
const ReportTemplates = () => {
  const navigate = useNavigate();
  const { userGoogleId: accessToken } = DataState();

  // --- Template List State ---
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);

  // --- Preview State ---
  const [previewSections, setPreviewSections] = useState([]);
  const [templateHeader, setTemplateHeader] = useState(null);
  const [templateFooter, setTemplateFooter] = useState(null);
  const [templateId, setTemplateId] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [htmlPreview, setHtmlPreview] = useState('');

  // --- Inline Edit State ---
  const [activeSectionIndex, setActiveSectionIndex] = useState(null);
  const [insertAtIndex, setInsertAtIndex] = useState(null);
  const [inlineLoading, setInlineLoading] = useState(false);

  // --- Upload State ---
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // --- Chat State (for upload parsing) ---
  const [chatTaskId, setChatTaskId] = useState(null);

  // --- Load templates ---
  const fetchTemplates = useCallback(async () => {
    if (!accessToken) return;
    setLoadingTemplates(true);
    try {
      const data = await listReportTemplates(accessToken);
      setTemplates(data?.templates || []);
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // --- Load full template when selected from sidebar ---
  const loadTemplatePreview = useCallback(
    async (template) => {
      setSelectedTemplate(template);
      setActiveSectionIndex(null);
      setInsertAtIndex(null);
      try {
        const full = await getReportTemplate(accessToken, template._id);
        setPreviewSections(full.sections || []);
        setTemplateHeader(full.header || null);
        setTemplateFooter(full.footer || null);
        setTemplateId(full._id);
        setTemplateName(full.template_name || '');
        setHtmlPreview(full.html_preview || '');
      } catch (err) {
        toast.error('Failed to load template');
      }
    },
    [accessToken],
  );

  // --- Handle file upload → parse via direct auto-parse API (no LLM agent) ---
  const handleUpload = async (file) => {
    if (!file) {
      toast.error('No file detected by the browser.');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.docx')) {
      toast.error(
        `Invalid file type: ${file.name}. Please upload a .docx file`,
      );
      return;
    }
    setIsUploading(true);
    setPreviewSections([]);
    setTemplateHeader(null);
    setTemplateFooter(null);
    setTemplateId(null);
    setHtmlPreview('');
    setSelectedTemplate(null);

    toast.success('Starting upload and parsing process...');

    try {
      const result = await autoParseTemplate(accessToken, file);

      if (result?.error) {
        toast.error(result.error);
        setIsUploading(false);
        return;
      }

      toast.success(result?.message || 'Template parsed and saved!');

      // Refresh template list and load the newly created template
      await fetchTemplates();
      if (result?.template_id) {
        try {
          const full = await getReportTemplate(accessToken, result.template_id);
          setPreviewSections(full.sections || []);
          setTemplateHeader(full.header || null);
          setTemplateFooter(full.footer || null);
          setTemplateId(full._id);
          setTemplateName(full.template_name || '');
          setHtmlPreview(full.html_preview || '');
          setSelectedTemplate(full);
        } catch (err) {
          // Fallback: load the first template from the list
          const data = await listReportTemplates(accessToken);
          const all = data?.templates || [];
          if (all.length > 0) loadTemplatePreview(all[0]);
        }
      } else {
        // Fallback: load the first template from the list
        const data = await listReportTemplates(accessToken);
        const all = data?.templates || [];
        if (all.length > 0) loadTemplatePreview(all[0]);
      }
    } catch (err) {
      toast.error('Failed to upload and parse template.');
    } finally {
      setIsUploading(false);
    }
  };

  // --- Drag and Drop ---
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = () => setIsDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  // --- Section click ---
  const handleSectionClick = (index) => {
    setInsertAtIndex(null);
    setActiveSectionIndex(activeSectionIndex === index ? null : index);
  };

  // --- Divider click ---
  const handleDividerClick = (index, e) => {
    e.stopPropagation();
    setActiveSectionIndex(null);
    setInsertAtIndex(insertAtIndex === index ? null : index);
  };

  // --- Helper to stream AI updates ---
  const streamAITemplateUpdate = async (promptMessage, successMessage) => {
    try {
      let taskId = chatTaskId;
      if (!taskId) {
        const taskData = await createTask(
          {
            task: `Template Builder: Update Template`,
            agent_sequence: ['TemplateBuilder'],
          },
          accessToken,
        );
        taskId = taskData._id || taskData.task_id;
        setChatTaskId(taskId);
      }

      const chatsPayload = [
        {
          role: 'user',
          content: [{ type: 'text', text: promptMessage }],
        },
      ];
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const initResponse = await initializeStreamChat(
        {
          chats: chatsPayload,
          task_id: taskId,
          is_agent_mode: true,
          agent_sequence: ['TemplateBuilder'],
          timezone,
        },
        accessToken,
      );

      const eventSource = streamChat(initResponse.stream_id, accessToken);
      let streamEnded = false;

      return new Promise((resolve, reject) => {
        eventSource.addEventListener('streamEnd', () => {
          if (streamEnded) return;
          streamEnded = true;
          eventSource.close();
          setTimeout(async () => {
            await fetchTemplates();
            if (templateId) {
              try {
                const full = await getReportTemplate(accessToken, templateId);
                setPreviewSections(full.sections || []);
                setTemplateHeader(full.header || null);
                setTemplateFooter(full.footer || null);
              } catch (err) {
                toast.error('Failed to reload template after AI update');
              }
            }
            toast.success(successMessage);
            resolve();
          }, 1000);
        });

        eventSource.onerror = () => {
          if (streamEnded) return;
          streamEnded = true;
          eventSource.close();
          toast.error('Error updating template via AI.');
          reject(new Error('Stream error'));
        };
      });
    } catch (err) {
      toast.error('Failed to update template via AI');
      throw err;
    }
  };

  // --- Submit section edit ---
  const handleSectionEdit = async ({ title, instruction, styling }) => {
    if (!templateId || activeSectionIndex === null) return;
    setInlineLoading(true);
    try {
      const cur = previewSections[activeSectionIndex];
      const promptMessage = `I am modifying my report template (ID: ${templateId}).
I want to EDIT the section at index ${activeSectionIndex} (Current Title: "${cur.title || cur.heading}").

User Instructions for AI for this section:
New Title: "${title}"
Content Points: "${instruction}"
Formatting & Styling Requirements: "${styling || 'None specified'}"

Please understand these instructions, optimize the section title and description if necessary (incorporating BOTH the content points and styling requirements into the saved section description) to make it a great report template section, and use the \`update_report_template\` tool to save the changes. 

IMPORTANT: The \`update_report_template\` tool requires the FULL list of sections to replace the existing ones. You MUST include all other existing sections unmodified in your tool call array, and only modify the one at index ${activeSectionIndex}. 
Here is the current full list of sections for your reference:
${JSON.stringify(previewSections, null, 2)}`;

      await streamAITemplateUpdate(promptMessage, 'Section updated via AI!');
      setActiveSectionIndex(null);
    } catch (err) {
      toast.error('Failed to update section via AI');
    } finally {
      setInlineLoading(false);
    }
  };

  // --- Submit section insert ---
  const handleSectionInsert = async ({ title, instruction, styling }) => {
    if (!templateId || insertAtIndex === null) return;
    setInlineLoading(true);
    try {
      const promptMessage = `I am modifying my report template (ID: ${templateId}).
I want to INSERT a new section at index ${insertAtIndex}.

User Instructions for AI for this NEW section:
Title: "${title}"
Content Points: "${instruction}"
Formatting & Styling Requirements: "${styling || 'None specified'}"

Please understand these instructions, optimize the section title and description if necessary (incorporating BOTH the content points and styling requirements into the saved section description) to make it a great report template section, and use the \`update_report_template\` tool to save the changes. 

IMPORTANT: The \`update_report_template\` tool requires the FULL list of sections to replace the existing ones. You MUST include all other existing sections unmodified in your tool call array, and insert the new section at index ${insertAtIndex}. 
Here is the current full list of sections before insertion for your reference:
${JSON.stringify(previewSections, null, 2)}`;

      await streamAITemplateUpdate(promptMessage, 'Section inserted via AI!');
      setInsertAtIndex(null);
    } catch (err) {
      toast.error('Failed to insert section via AI');
    } finally {
      setInlineLoading(false);
    }
  };

  // --- Delete ---
  const handleDelete = async (tid) => {
    try {
      await deleteReportTemplate(accessToken, tid);
      toast.success('Template deleted');
      fetchTemplates();
      setMenuOpenId(null);
      if (selectedTemplate?._id === tid) {
        setSelectedTemplate(null);
        setPreviewSections([]);
        setTemplateId(null);
        setHtmlPreview('');
      }
    } catch (err) {
      toast.error('Failed to delete template');
    }
  };

  // --- Toggle ---
  const handleToggleStatus = async (tid) => {
    try {
      const result = await toggleReportTemplateStatus(accessToken, tid);
      toast.success(result.message || 'Status toggled');
      fetchTemplates();
      setMenuOpenId(null);
    } catch (err) {
      toast.error('Failed to toggle status');
    }
  };

  // --- New template ---
  const handleNewTemplate = () => {
    setSelectedTemplate(null);
    setPreviewSections([]);
    setTemplateHeader(null);
    setTemplateFooter(null);
    setTemplateId(null);
    setTemplateName('');
    setHtmlPreview('');
    setActiveSectionIndex(null);
    setInsertAtIndex(null);
  };

  const hasPreview = previewSections.length > 0 || !!htmlPreview;

  // =====================================================================
  // RENDER
  // =====================================================================
  return (
    <div className="flex h-screen w-full overflow-hidden bg-[#0a0a0c] text-zinc-200">
      <style>
        {`
                .mammoth-preview p { margin-bottom: 1em; line-height: 1.6; }
                .mammoth-preview h1, .mammoth-preview h2, .mammoth-preview h3, .mammoth-preview h4 { margin-top: 1.5em; margin-bottom: 0.5em; font-weight: bold; }
                .mammoth-preview h1 { font-size: 2em; }
                .mammoth-preview h2 { font-size: 1.5em; }
                .mammoth-preview table { border-collapse: collapse; width: 100%; margin-bottom: 1em; }
                .mammoth-preview td, .mammoth-preview th { border: 1px solid #ddd; padding: 8px; }
                .mammoth-preview img { max-width: 100%; height: auto; display: block; margin: 1em auto; }
                .mammoth-preview ul, .mammoth-preview ol { margin-left: 2em; margin-bottom: 1em; }
                .mammoth-preview li { margin-bottom: 0.5em; }
                `}
      </style>

      {/* ======================== LEFT SIDEBAR ======================== */}
      <div className="flex h-full w-72 flex-shrink-0 flex-col border-r border-zinc-800 bg-[#111114]">
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/chat')}
              className="rounded-lg p-1.5 transition-colors hover:bg-zinc-800"
              title="Back to Chat"
            >
              <IoArrowBack className="text-lg text-zinc-400" />
            </button>
            <h2 className="text-sm font-bold text-zinc-100">
              Report Templates
            </h2>
          </div>
          <button
            onClick={handleNewTemplate}
            className="rounded-lg border border-[#5652CF]/30 bg-[#5652CF]/20 p-1.5 transition-all hover:bg-[#5652CF]/30"
            title="New Template"
          >
            <IoAddCircleOutline className="text-lg text-[#5652CF]" />
          </button>
        </div>

        <div className="custom-scrollbar flex-1 space-y-2 overflow-y-auto p-3">
          {loadingTemplates ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-[#5652CF]" />
              <p className="mt-2 text-xs text-zinc-500">Loading templates...</p>
            </div>
          ) : templates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <HiOutlineTemplate className="mb-3 h-10 w-10 text-zinc-700" />
              <p className="text-sm font-medium text-zinc-400">
                No templates yet
              </p>
              <p className="mt-1 text-xs text-zinc-600">
                Upload a .docx to get started
              </p>
            </div>
          ) : (
            templates.map((template) => (
              // eslint-disable-next-line
              <div
                key={template._id}
                className={`group relative cursor-pointer rounded-xl border p-3 transition-all duration-200 ${
                  selectedTemplate?._id === template._id
                    ? 'border-[#5652CF]/30 bg-[#5652CF]/10'
                    : 'border-zinc-800/50 bg-zinc-900/50 hover:border-zinc-700 hover:bg-zinc-800/50'
                }`}
                onClick={() => loadTemplatePreview(template)}
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <IoDocumentTextOutline className="flex-shrink-0 text-sm text-zinc-400" />
                      <h3 className="truncate text-sm font-semibold text-zinc-200">
                        {template.template_name}
                      </h3>
                    </div>
                    <div className="mt-1.5 flex items-center gap-2">
                      <span className="rounded-full border border-zinc-700 bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-400">
                        {template.template_type?.replace('_', ' ')}
                      </span>
                      <span className="text-[10px] text-zinc-600">
                        {template.section_count} sections
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-1.5">
                      {template.is_active ? (
                        <span className="flex items-center gap-1 text-[10px] text-emerald-400">
                          <IoCheckmarkCircle className="text-xs" /> Active
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-[10px] text-zinc-500">
                          <IoCloseCircle className="text-xs" /> Inactive
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenId(
                          menuOpenId === template._id ? null : template._id,
                        );
                      }}
                      className="rounded-lg p-1 opacity-0 transition-all hover:bg-zinc-700 group-hover:opacity-100"
                    >
                      <IoEllipsisVertical className="text-sm text-zinc-400" />
                    </button>
                    {menuOpenId === template._id && (
                      <div className="absolute right-0 top-8 z-20 w-40 rounded-lg border border-zinc-700 bg-zinc-800 py-1 shadow-xl">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleStatus(template._id);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-zinc-700"
                        >
                          <IoToggle className="text-sm" />
                          {template.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(template._id);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-red-400 hover:bg-zinc-700"
                        >
                          <IoTrashOutline className="text-sm" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* =================== CENTER: DOCUMENT PREVIEW =================== */}
      <div className="flex h-full min-w-0 flex-1 flex-col bg-[#1a1a20]">
        {/* Top bar */}
        <div className="flex items-center justify-between border-b border-zinc-800 bg-[#111114] px-6 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-[#5652CF] to-indigo-500 shadow-lg shadow-[#5652CF]/20">
              <HiOutlineTemplate className="text-sm text-white" />
            </div>
            <div>
              <h1 className="text-sm font-bold text-zinc-100">
                {templateName || 'Template Preview'}
              </h1>
              <p className="text-[10px] text-zinc-500">
                {hasPreview
                  ? 'Visual Preview of the Document'
                  : 'Upload a .docx file to get started'}
              </p>
            </div>
          </div>
          {hasPreview && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-xs text-zinc-300 transition-colors hover:bg-zinc-700"
            >
              <IoCloudUploadOutline className="text-sm" />
              Upload New
            </button>
          )}
        </div>

        {/* Content Area */}
        <div
          className="custom-scrollbar flex-1 overflow-y-auto"
          style={{ background: '#2a2a32' }}
        >
          {!hasPreview && !isUploading ? (
            /* ========== UPLOAD EMPTY STATE ========== */
            <div className="flex h-full flex-col items-center justify-center px-6">
              {/* eslint-disable-next-line */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`group w-full max-w-lg cursor-pointer rounded-2xl border-2 border-dashed p-12 transition-all duration-300 ${
                  isDragOver
                    ? 'scale-[1.02] border-[#5652CF] bg-[#5652CF]/10'
                    : 'border-zinc-600 hover:border-[#5652CF]/50 hover:bg-zinc-800/30'
                }`}
              >
                <div className="flex flex-col items-center text-center">
                  <div
                    className={`mb-6 flex h-16 w-16 items-center justify-center rounded-2xl transition-all ${
                      isDragOver
                        ? 'border border-[#5652CF]/30 bg-[#5652CF]/20'
                        : 'border border-zinc-700 bg-zinc-800 group-hover:bg-[#5652CF]/10'
                    }`}
                  >
                    <IoCloudUploadOutline
                      className={`text-3xl transition-colors ${isDragOver ? 'text-[#5652CF]' : 'text-zinc-400 group-hover:text-[#5652CF]'}`}
                    />
                  </div>
                  <h2 className="mb-2 text-lg font-bold text-zinc-100">
                    Upload a Report Template
                  </h2>
                  <p className="mb-4 max-w-sm text-sm text-zinc-500">
                    Drag and drop a .docx file here, or click to browse. We'll
                    parse the exact styling and structure.
                  </p>
                  <span className="rounded-full border border-zinc-700 bg-zinc-800 px-3 py-1 text-xs text-zinc-400">
                    .docx files only
                  </span>
                </div>
              </div>
            </div>
          ) : isUploading ? (
            /* ========== PARSING STATE ========== */
            <div className="flex h-full flex-col items-center justify-center px-6">
              <Loader2 className="mb-4 h-10 w-10 animate-spin text-[#5652CF]" />
              <h2 className="mb-2 text-lg font-bold text-zinc-100">
                Parsing Template...
              </h2>
              <p className="text-sm text-zinc-500">
                Extracting exact HTML styling and structural sections
              </p>
            </div>
          ) : (
            /* ========== VISUAL HTML PREVIEW ========== */
            <TemplateStructuralPreview
              templateName={templateName}
              header={templateHeader}
              footer={templateFooter}
              sections={previewSections}
            />
          )}
          {/* Hidden input kept outside conditionals so ref is always valid */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".docx"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleUpload(f);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      {/* ======================== RIGHT SIDEBAR: STRUCTURE ======================== */}
      {hasPreview && (
        <div className="flex h-full w-[350px] flex-shrink-0 flex-col border-l border-zinc-800 bg-[#111114]">
          <div className="z-10 border-b border-zinc-800 px-5 py-4 shadow-sm">
            <h2 className="text-sm font-bold text-zinc-100">
              Template Structure
            </h2>
            <p className="mt-1 text-[10px] leading-relaxed text-zinc-500">
              Interact with the section structure below. Click to edit
              instructions for generating each section.
            </p>
          </div>

          <div className="custom-scrollbar flex-1 overflow-y-auto bg-[#0c0c0f] p-5">
            {/* Insert top divider */}
            <div
              className="group/divider relative"
              style={{ padding: '8px 0' }}
            >
              <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover/divider:opacity-100">
                <button
                  onClick={(e) => handleDividerClick(0, e)}
                  className={`flex h-6 w-6 items-center justify-center rounded-full border transition-all ${
                    insertAtIndex === 0
                      ? 'border-[#5652CF] bg-[#5652CF] text-white shadow-lg shadow-[#5652CF]/20'
                      : 'border-zinc-600 bg-zinc-800 text-zinc-400 hover:border-[#5652CF] hover:text-[#5652CF]'
                  }`}
                >
                  <Plus className="h-3 w-3" />
                </button>
              </div>
              {insertAtIndex === 0 && (
                <SectionEditForm
                  initialTitle=""
                  initialInstruction=""
                  isEdit={false}
                  onSubmit={handleSectionInsert}
                  onCancel={() => setInsertAtIndex(null)}
                  isLoading={inlineLoading}
                />
              )}
            </div>

            {previewSections.map((section, idx) => (
              <React.Fragment key={idx}>
                {/* eslint-disable-next-line */}
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSectionClick(idx);
                  }}
                  className={`relative cursor-pointer rounded-xl border p-4 transition-all duration-200 ${
                    activeSectionIndex === idx
                      ? 'border-[#5652CF]/50 bg-[#5652CF]/10 shadow-lg shadow-[#5652CF]/10'
                      : 'border-zinc-800/80 bg-zinc-900/60 hover:border-zinc-700 hover:bg-zinc-800/80'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded text-[10px] font-bold ${
                        activeSectionIndex === idx
                          ? 'border border-[#5652CF]/30 bg-[#5652CF]/20 text-[#5652CF]'
                          : 'border border-zinc-700 bg-zinc-800 text-zinc-500'
                      }`}
                    >
                      {idx + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3
                        className={`mb-1 text-sm font-semibold ${activeSectionIndex === idx ? 'text-[#5652CF]' : 'text-zinc-200'}`}
                      >
                        {section.title ||
                          section.heading ||
                          `Section ${idx + 1}`}
                      </h3>
                      {(section.description || section.content_summary) && (
                        <p className="line-clamp-3 max-h-20 overflow-hidden text-[11px] leading-relaxed text-zinc-500">
                          {section.description || section.content_summary}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Inline Chat for Editing */}
                  {activeSectionIndex === idx && (
                    <div className="relative z-20 mt-3">
                      <SectionEditForm
                        initialTitle={section.title || section.heading}
                        initialInstruction={
                          section.description || section.content_summary
                        }
                        initialStyling={section.styling || ''}
                        isEdit={true}
                        onSubmit={handleSectionEdit}
                        onCancel={() => setActiveSectionIndex(null)}
                        isLoading={inlineLoading}
                      />
                    </div>
                  )}
                </div>

                {/* Divider below section */}
                <div
                  className="group/divider relative"
                  style={{ padding: '8px 0' }}
                >
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover/divider:opacity-100">
                    <button
                      onClick={(e) => handleDividerClick(idx + 1, e)}
                      className={`flex h-6 w-6 items-center justify-center rounded-full border transition-all ${
                        insertAtIndex === idx + 1
                          ? 'border-[#5652CF] bg-[#5652CF] text-white shadow-lg shadow-[#5652CF]/20'
                          : 'border-zinc-600 bg-zinc-800 text-zinc-400 hover:border-[#5652CF] hover:text-[#5652CF]'
                      }`}
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                  {insertAtIndex === idx + 1 && (
                    <SectionEditForm
                      initialTitle=""
                      initialInstruction=""
                      isEdit={false}
                      onSubmit={handleSectionInsert}
                      onCancel={() => setInsertAtIndex(null)}
                      isLoading={inlineLoading}
                    />
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Close menu overlay */}
      {menuOpenId && (
        //eslint-disable-next-line
        <div
          className="fixed inset-0 z-10"
          onClick={() => setMenuOpenId(null)}
        />
      )}

      {/* Hidden file input for "Upload New" */}
      {hasPreview && (
        <input
          ref={fileInputRef}
          type="file"
          accept=".docx"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleUpload(f);
            e.target.value = '';
          }}
        />
      )}
    </div>
  );
};

export default ReportTemplates;
